// frontend/src/App.js
import React, { useState, useEffect, useRef } from 'react';
import {
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  Container,
  Grid,
  Avatar
} from '@mui/material';
import { styled, ThemeProvider } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

import { lightTheme, darkTheme } from './theme';
import API_URL from './config';

import Login from './components/Login';
import Register from './components/Register';
import AtendimentoForm from './components/AtendimentoForm';
import AtendimentoList from './components/AtendimentoList';
import UserManagement from './components/UserManagement';
import CategoryManagement from './components/CategoryManagement';
import ReportDashboard from './components/ReportDashboard';
import PlantaoAgenda from './components/PlantaoAgenda';
import Sidebar from './components/Sidebar';
import { parseJwt } from './utils/jwt';
import { readError } from './utils/api';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 72;

const StyledPaper = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  borderRadius: 12,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 10px rgba(0,0,0,0.5)'
    : '0 2px 10px rgba(0,0,0,0.06)',
  backgroundColor: theme.palette.background.paper,
}));

export default function App() {
  const [mode, setMode] = useState(localStorage.getItem('mode') || 'light');
  const theme = mode === 'light' ? lightTheme : darkTheme;
  const toggleColorMode = () => {
    const next = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    localStorage.setItem('mode', next);
  };

  const [token, setToken] = useState(
    localStorage.getItem('token') || sessionStorage.getItem('token')
  );
  const [user, setUser] = useState(null);
  const [view, setView] = useState('login');
  const [mobileOpen, setMobileOpen] = useState(false);

  // recolhe o menu lateral no desktop (preferência salva no navegador)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === '1'
  );
  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem('sidebarCollapsed', next ? '1' : '0');
      return next;
    });
  };
  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  // referência do timer de logout
  const logoutTimer = useRef(null);

  // sempre que token mudar, define usuário e view
  useEffect(() => {
    if (token) {
      setUser(parseJwt(token));
      setView('atendimentos');
    } else {
      setUser(null);
      setView('login');
    }
  }, [token]);

  // dispara no login: guarda token e timestamp
  const handleLogin = (t, remember) => {
    const now = Date.now().toString();
    if (remember) {
      localStorage.setItem('token', t);
      localStorage.setItem('loginTime', now);
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('loginTime');
    } else {
      sessionStorage.setItem('token', t);
      sessionStorage.setItem('loginTime', now);
      localStorage.removeItem('token');
      localStorage.removeItem('loginTime');
    }
    setToken(t);
  };

  // limpa tudo no logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('loginTime');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('loginTime');
    setToken(null);
    setMobileOpen(false);
  };

  // Qualquer resposta 401 da API (sessão expirada, usuário inativado ou token
  // inválido) desloga, em todas as telas. O login em si (/api/auth/) fica de fora.
  const handleLogoutRef = useRef(handleLogout);
  handleLogoutRef.current = handleLogout;
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      const target = args[0];
      const url = typeof target === 'string' ? target : (target && target.url) || '';
      if (res.status === 401 && !url.includes('/api/auth/')) {
        handleLogoutRef.current();
      }
      return res;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // agenda logout automático 1h após loginTime
  useEffect(() => {
    if (token) {
      // pega loginTime de onde estiver
      const storedTime =
        localStorage.getItem('loginTime') || sessionStorage.getItem('loginTime') || '0';
      const loginTime = parseInt(storedTime, 10);
      const elapsed = Date.now() - loginTime;
      const maxDur = 60 * 60 * 1000; // 1h em ms
      const remaining = maxDur - elapsed;

      if (remaining <= 0) {
        handleLogout();
      } else {
        logoutTimer.current = setTimeout(handleLogout, remaining);
      }
    }
    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [token]);

  // itens de menu
  const menuItems = [
    { key: 'atendimentos', icon: <EventIcon />, label: 'Atendimentos' },
    { key: 'plantao', icon: <EventAvailableIcon />, label: 'Agenda' },
    {
      key: 'categories',
      icon: <CategoryIcon />,
      label: 'Categorias',
      auth: ['DEV', 'SAF'].includes(user?.sector)
    },
    {
      key: 'users',
      icon: <PeopleIcon />,
      label: 'Usuários',
      auth: user?.sector === 'DEV'
    },
    { key: 'reports', icon: <BarChartIcon />, label: 'Relatórios' }
  ];

  const sidebarProps = {
    user,
    menuItems,
    view,
    onSelect: key => {
      setView(key);
      setMobileOpen(false);
    },
    onLogout: handleLogout,
    onToggleCollapsed: toggleCollapsed
  };


  // estados e funções de atendimentos, relatórios etc.
  const [atendimentos, setAtendimentos] = useState([]);
  const [editingAtendimento, setEditingAtendimento] = useState(null);

  const fetchAtendimentos = () => {
    if (!token) return;
    fetch(`${API_URL}/api/atendimentos`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(res => {
        if (res.status === 401) {
          handleLogout();
          return Promise.reject();
        }
        return res.ok ? res.json() : Promise.reject();
      })
      .then(data => setAtendimentos(data))
      .catch(() => console.log('Falha ao carregar atendimentos.'));
  };

  useEffect(() => {
    if (token) fetchAtendimentos();
  }, [token]);

  const handleDelete = id => {
    if (!window.confirm('Confirma exclusão?')) return;
    fetch(`${API_URL}/api/atendimentos/${id}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token }
    }).then(async res => {
      if (res.status === 401) {
        handleLogout();
        return;
      }
      if (!res.ok) {
        alert(await readError(res, 'Não foi possível excluir o atendimento.'));
        return;
      }
      fetchAtendimentos();
    });
  };

  // gera o PDF com os mesmos filtros aplicados na tabela de atendimentos
  const generateReport = ({ atendente, loja, de, ate }) => {
    const params = new URLSearchParams();
    if (atendente) params.set('atendente', atendente);
    if (loja) params.set('loja', loja);
    if (de) params.set('de', de);
    if (ate) params.set('ate', ate);

    fetch(`${API_URL}/api/atendimentos/report?${params.toString()}`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(async res => {
        if (res.status === 401) {
          handleLogout();
          return null;
        }
        if (!res.ok) {
          throw new Error(
            res.status === 404 && !(res.headers.get('content-type') || '').includes('json')
              ? `Erro 404: o servidor não tem a rota de relatório. Reinicie/atualize o backend.`
              : await readError(res, 'Erro ao gerar relatório')
          );
        }
        return res.blob();
      })
      .then(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio-atendimentos-${de || ate || new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(err => alert(err.message || 'Erro ao gerar relatório'));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: theme => theme.zIndex.drawer + 1,
            ml: { md: `${drawerWidth}px` },
            width: { md: user ? `calc(100% - ${drawerWidth}px)` : '100%' },
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            transition: theme =>
              theme.transitions.create(['width', 'margin'], {
                duration: theme.transitions.duration.shorter
              })
          }}
        >
          <Toolbar>
            {user && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(o => !o)}
                sx={{ mr: 2, display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 32,
                height: 32,
                mr: 1.5,
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              <EventIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700 }}>
              Sistema de Atendimentos
            </Typography>
            <IconButton onClick={toggleColorMode}>
              {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {user && (
          <Box
            component="nav"
            sx={{
              width: { md: drawerWidth },
              flexShrink: { md: 0 },
              transition: theme =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter
                })
            }}
          >
            {/* Drawer temporário: sobrepõe o conteúdo em telas pequenas (sempre expandido) */}
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box'
                }
              }}
            >
              <Sidebar
                isCollapsed={false}
                {...sidebarProps}
              />
            </Drawer>

            {/* Drawer fixo: sempre visível em telas médias/grandes, recolhível */}
            <Drawer
              variant="permanent"
              open
              sx={{
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                  transition: theme =>
                    theme.transitions.create('width', {
                      duration: theme.transitions.duration.shorter
                    })
                }
              }}
            >
              <Sidebar
                isCollapsed={collapsed}
                showCollapseToggle
                {...sidebarProps}
              />
            </Drawer>
          </Box>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            mt: 8,
            width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
            maxWidth: 1600,
            mx: 'auto',
            transition: theme =>
              theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter
              })
          }}
        >
          {!user ? (
            <Container
              maxWidth="xs"
              sx={{
                mt: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              {view === 'login' ? (
                <Login onLogin={handleLogin} showRegister={() => setView('register')} />
              ) : (
                <Register showLogin={() => setView('login')} />
              )}
            </Container>
          ) : (
            <>
              {view === 'atendimentos' && (
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12 }}>
                    <StyledPaper>
                      <AtendimentoForm
                        token={token}
                        atendente={user.username}
                        editingAtendimento={editingAtendimento}
                        onAdd={fetchAtendimentos}
                        onUpdate={() => {
                          fetchAtendimentos();
                          setEditingAtendimento(null);
                        }}
                        clearEditing={() => setEditingAtendimento(null)}
                      />
                    </StyledPaper>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <StyledPaper>
                      <Typography variant="h6" sx={{ mb: 2 }}>Atendimentos</Typography>
                      <AtendimentoList
                        atendimentos={atendimentos}
                        onEdit={row => setEditingAtendimento(row)}
                        onDelete={handleDelete}
                        canModify={row => user?.sector === 'DEV' || row.atendente === user?.username}
                        onGenerateReport={generateReport}
                      />
                    </StyledPaper>
                  </Grid>
                </Grid>
              )}
              {view === 'plantao' && (
                <StyledPaper>
                  <PlantaoAgenda token={token} user={user} />
                </StyledPaper>
              )}
              {view === 'categories' && (
                <StyledPaper>
                  <CategoryManagement token={token} />
                </StyledPaper>
              )}
              {view === 'users' && (
                <StyledPaper>
                  <UserManagement token={token} />
                </StyledPaper>
              )}
              {view === 'reports' && (
                <StyledPaper>
                  <ReportDashboard token={token} />
                </StyledPaper>
              )}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
